import {
  isAutoModeTemplateId,
  markOfflineLocalSession,
} from '@/utils/offline-recording/localSessionSettings';
import { GenerationMethod, RecordingStatus } from '@heidi/schema/ml-scribe';
import {
  BackupUploadingStatus,
  type QuerySessionResponse,
  // Straight from the contracts door rather than via `@/models/scribeRecordModel`,
  // which re-exports it: that module pulls the Rematch store into this file's
  // import graph, and importing this builder from the session page was enough to
  // reorder store initialisation into a TDZ crash. The enum itself is a leaf.
  StreamingStatus,
} from '@heidi/session/contracts';

/**
 * The per-session facts an offline-local stub carries. All optional, and all
 * nullable so the durable list rows (SQL NULL for anything the user never set)
 * can be forwarded verbatim without per-field massaging at the call site.
 */
export interface OfflineLocalSessionStubSettings {
  transcribeMode?: string | null;
  language?: string | null;
  outputLanguage?: string | null;
  templateId?: string | null;
  noteStyle?: string | null;
  /**
   * Total audio this consult already holds, in MILLISECONDS, summed across its
   * recordings by Rust (`offline_sessions.recorded_duration_ms`).
   *
   * Not a setting — a fact about what has been captured — and the one fact that
   * makes a rebuilt stub say "stopped with audio" rather than "never recorded".
   * Written onto the stub as `duration` (SECONDS), which is the field the
   * shared recording timer is seeded from (`useUpdateTimerFromSession`) and the
   * field the online stop path gets from the server.
   *
   * Omitted by the create path, which mints a consult that holds nothing.
   */
  recordedDurationMs?: number | null;
  /**
   * What the clinician answered on the patient-consent gate
   * (`offline_sessions.is_consent`).
   *
   * Not a setting either — a compliance fact about the encounter — and the last
   * durable per-session fact this builder did not read back. Every consent
   * reader (`useRecordingButton`, `useCheckIsBlockedOnConsent` and their twins)
   * resolves the gate from the session's cached entry, so a stub rebuilt without
   * it re-asks a clinician who has already answered, against an answer this
   * device is holding on disk.
   *
   * Three-state, and the states must stay apart on the way in: `null`/absent is
   * "the gate never ran", `false` is a refusal. See {@link definedConsent}.
   *
   * Omitted by the create path, which mints a consult nobody has been asked
   * about yet.
   */
  isConsent?: boolean | null;
}

/**
 * Collapses "no value" (SQL NULL from the durable row, or a cleared setting that
 * round-trips as an empty string) to `undefined`, so an unset setting lands on
 * the stub as *absent* and its picker falls back to its own default instead of
 * rendering an empty selection. Applied here, not at each caller, so the two
 * writers below cannot normalise differently.
 */
function definedSetting(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Collapses "the consent gate never ran" (SQL NULL from the durable row, which
 * reaches the bridge as `null`) to `undefined`, and passes a real answer —
 * INCLUDING `false` — through untouched.
 *
 * Deliberately NOT {@link definedSetting}'s shape, and deliberately not a
 * `Boolean(...)` or a `?? false`. Either would land a never-asked consult on
 * `false`, minting a refusal nobody gave; a truthiness filter would do the
 * mirror-image damage and drop a refusal the clinician did give. `false` is an
 * answer, and the only reason it survives the rest of the app being unable to
 * tell it from silence is that nothing on the way in has flattened it yet.
 *
 * Which of the two the gate should DO is a separate question, and an open one:
 * `!session?.is_consent` treats `false` and `undefined` identically, so a
 * refusal re-prompts today exactly as an unanswered consult does. Whether it
 * should is clinical policy, not a coding decision, and nothing here changes it.
 * This function's whole job is to make sure the answer is still in the data when
 * that policy is settled.
 *
 * THIS DOCBLOCK IS THE CANONICAL STATEMENT of the three-state rule. It is
 * restated nowhere at length: the other readers on this path carry one line and
 * point here. If the policy question is ever settled and readers start telling
 * `false` from `undefined`, this is the one place that has to change, and the
 * pointers are how the rest are found.
 */
function definedConsent(value: boolean | null | undefined): boolean | undefined {
  return value ?? undefined;
}

/**
 * Builds the React-Query stub for an offline-local session (marked so
 * `useQuerySessionById` stays disabled and never 404s the local id against the
 * server). Shared by the two writers so their stubs can't drift:
 *
 * - {@link useMutateCreateOfflineSession} on create (live, this process), and
 * - {@link useOfflineRecordingEvents} on cold-boot rehydration, where the
 *   in-memory stub was lost on quit and must be reconstructed from the durable
 *   Rust list so a session interacted with offline stays reachable after a
 *   relaunch instead of rendering the full-page 404 (APP-7910).
 *
 * On the rehydration path it also carries `duration`, and that is not cosmetic.
 * `duration` is the ONLY durable statement a consult makes about having been
 * recorded and stopped: `useUpdateTimerFromSession` seeds the shared timer from
 * it, and `useRecordingButton`'s offline start falls back to it when the Rematch
 * timer reads zero — which it always does in a new run of the app, because that
 * timer is in memory. A stub without it reopens a stopped two-part encounter
 * showing 00:00 and starts the second recording's timer from zero, which is what
 * a clinician who quit the app between halves of a consult actually sees.
 *
 * Deliberately DOES NOT set `created_time_local` / `created_at`. The session
 * date/time picker parses `created_time_local` as `new Date(value + 'Z')`; an
 * ISO value already ending in `Z` becomes `"…Z" + "Z"` → Invalid Date →
 * `RangeError` crashing the page (APP-7792). Leaving it unset makes the picker
 * fall back to `new Date()`. Sync replaces this stub with the real server
 * record (correct timestamps) once it completes.
 */
export function buildOfflineLocalSessionStub(
  localSessionId: string,
  settings: OfflineLocalSessionStubSettings = {}
): QuerySessionResponse {
  return markOfflineLocalSession(buildOfflineSessionFields(localSessionId, settings));
}

/**
 * The same session fields as {@link buildOfflineLocalSessionStub}, WITHOUT the
 * offline-local marker.
 *
 * The marker does two things: it disables `useQuerySessionById` for that id, and
 * it makes the id cross-match the offline queue everywhere. Both are right for a
 * local id and both are wrong for a SERVER id — marking a server session
 * offline-local is exactly the hijack that had to be fixed once already, because the real query
 * never runs again for the rest of the process.
 *
 * So the unmarked shape exists for one caller: rebuilding a server-backed
 * consult whose detail fetch has already FAILED with an unreachable backend,
 * while the device holds its audio. Leaving the query enabled means the real
 * session replaces this one the moment the backend answers, which is what makes
 * writing it safe at all.
 */
export function buildOfflineQueuedSessionStub(
  sessionId: string,
  settings: OfflineLocalSessionStubSettings = {}
): QuerySessionResponse {
  return buildOfflineSessionFields(sessionId, settings);
}

/**
 * The session fields both stub shapes share. Extracted so the marked and
 * unmarked builders above cannot drift: the ONLY difference between a rebuilt
 * local consult and a rebuilt server-backed one must be the marker.
 */
function buildOfflineSessionFields(
  sessionId: string,
  settings: OfflineLocalSessionStubSettings
): QuerySessionResponse {
  // `AUTO_MODE` in the durable row is the sentinel for "the clinician chose
  // Auto", not a template id, so render it as Auto with no template selected.
  //
  // The two writers encode Auto differently on purpose, and this decode has to
  // keep tolerating both:
  //
  // - create (`recorderSettings.ts`) leaves the template unset when the account
  //   default is Auto. An unset template means "the clinician chose nothing",
  //   and the sync-time merge backfills it from the account default.
  // - update (a switch to Auto during the session) writes this sentinel. A set
  //   template means "the clinician chose Auto for this session", and it wins
  //   over the account default at that same merge.
  //
  // That asymmetry is the whole point: if the account default is a template and
  // the clinician picks Auto offline, only the sentinel survives the merge.
  // Collapsing the sentinel back to an unset template would look like a tidy-up
  // and would silently restore the template Auto replaced.
  const storedTemplateId = definedSetting(settings.templateId);
  const isAutoMode = isAutoModeTemplateId(storedTemplateId);
  const templateId = isAutoMode ? undefined : storedTemplateId;
  // Seconds, matching `session.duration` everywhere else and the conversion the
  // live `pending_sync` handler does. Rounded, not floored, so a stub rebuilt
  // from the durable row and one written live from the same recording agree.
  // A null/zero/negative row value lands as `undefined`, not `0`: the create
  // path and a consult that was never stopped must stay indistinguishable from
  // each other. Written as an explicit `undefined` like every other unset field
  // on this stub, rather than a conditional spread — same thing at every read,
  // and one shape means the object's key set does not depend on its input.
  const recordedDurationMs = settings.recordedDurationMs;
  const durationSeconds =
    typeof recordedDurationMs === 'number' && recordedDurationMs > 0
      ? Math.round(recordedDurationMs / 1000)
      : undefined;
  return {
    session_id: sessionId,
    duration: durationSeconds,
    transcribe_mode: definedSetting(settings.transcribeMode),
    language_code: definedSetting(settings.language),
    output_language_code: definedSetting(settings.outputLanguage),
    consult_note: {
      template_id: templateId,
      voice_style: definedSetting(settings.noteStyle),
      generation_method: templateId
        ? GenerationMethod.TEMPLATE
        : GenerationMethod.AUTO_MODE,
    },
    // The stored consent answer, three states intact — see
    // `definedConsent`. Display/gate only: the durable column is what Rust
    // reads at drain time and puts on the session PATCH, so nothing is written
    // back from here.
    is_consent: definedConsent(settings.isConsent),
    recording_status: RecordingStatus.IDLE,
    streamingStatus: StreamingStatus.NOT_STARTED,
    backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
    isSocketError: false,
    userDismissedNoSoundDetectedModal: false,
    shouldShowNoteSyncChanges: false,
    shouldShowDocumentSyncChanges: false,
    abortController: new AbortController(),
    isDocumentFromNote: false,
    isAutoGeneratedNoteWarningDismissed: false,
  } as QuerySessionResponse;
}
